import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { Home, Building2, LandPlot, Clock, TrendingDown, MessageSquareWarning } from 'lucide-react';
import { API } from '@/lib/api';

import Sidebar from '@/components/Sidebar';
import FilterPanel from '@/components/FilterPanel';
import PropertyCard from '@/components/PropertyCard';
import PropertyDetailDialog from '@/components/PropertyDetailDialog';

const typeConfig = {
  haus: {
    color: '#0f9d94',
    icon: Home,
  },
  wohnung: {
    color: '#8b1e1e',
    icon: Building2,
  },
  grundstueck: {
    color: '#8b5a2b',
    icon: LandPlot,
  },
};

const markerIconCache = new Map();

const hasValidCoordinates = (property) => (
  Number.isFinite(Number(property?.latitude)) &&
  Number.isFinite(Number(property?.longitude))
);

const getMarketingFilterType = (property) => (
  String(property?.marketing_type || '').toUpperCase().includes('MIETE') ? 'rent' : 'sale'
);

const createMarkerIcon = (type, isActive = true) => {
  const cacheKey = `${type}:${isActive ? 'active' : 'inactive'}`;
  const cachedIcon = markerIconCache.get(cacheKey);
  if (cachedIcon) {
    return cachedIcon;
  }

  const config = typeConfig[type] || typeConfig.grundstueck;
  const Icon = config.icon;
  const color = isActive ? config.color : '#94A3B8';
  const borderColor = isActive ? 'white' : '#E2E8F0';
  const opacity = isActive ? 1 : 0.82;
  const iconMarkup = renderToStaticMarkup(<Icon size={16} strokeWidth={2.2} color="white" />);

  const icon = L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        background-color: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 3px solid ${borderColor};
        box-shadow: 0 4px 12px rgba(0,0,0,0.25);
        opacity: ${opacity};
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.2s ease;
        pointer-events: auto;
      ">
        ${iconMarkup}
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

  markerIconCache.set(cacheKey, icon);
  return icon;
};

function MapBoundsUpdater({ properties }) {
  const map = useMap();
  const lastBoundsKeyRef = useRef('');

  useEffect(() => {
    if (properties.length === 0) {
      return;
    }

    const boundsKey = properties.map((property) => property.property_id).join('|');
    if (lastBoundsKeyRef.current === boundsKey) {
      return;
    }

    lastBoundsKeyRef.current = boundsKey;
    const bounds = L.latLngBounds(properties.map((property) => [property.latitude, property.longitude]));
    map.fitBounds(bounds, { padding: [50, 50] });
  }, [properties, map]);

  return null;
}

function buildStats(properties) {
  const activeProperties = properties.filter((property) => property.is_active !== false);
  const historicalProperties = properties.filter((property) => property.is_historical === true || property.is_active === false);

  return {
    total: properties.length,
    houses: properties.filter((property) => property.property_type === 'haus').length,
    apartments: properties.filter((property) => property.property_type === 'wohnung').length,
    properties: properties.filter((property) => property.property_type === 'grundstueck').length,
    sale_total: properties.filter((property) => getMarketingFilterType(property) === 'sale').length,
    rent_total: properties.filter((property) => getMarketingFilterType(property) === 'rent').length,
    active_total: activeProperties.length,
    inactive_total: historicalProperties.length,
    historical_total: historicalProperties.length,
    all_time_total: properties.length,
  };
}

function formatCompactDate(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(date);
}

function PropertyMapStatusPanel({ status }) {
  if (!status) {
    return null;
  }

  const timeline = [...(status.events || [])]
    .reverse()
    .map((event, index) => ({
      name: event.event_label || event.event_type,
      value: event.event_type === 'price_change' ? 2 : event.event_type === 'feedback_submitted' ? 3 : 1,
      index,
    }));

  return (
    <div className="w-[min(78vw,260px)] border-l border-stone-200 bg-white p-3 sm:w-[260px] sm:p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">Admin-Status</p>
          <p className="text-sm font-bold text-slate-900">{status.current_status || 'Unbekannt'}</p>
        </div>
        {status.feedback_pending && (
          <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-800">
            Rückmeldung offen
          </span>
        )}
      </div>

      <div className="mb-3 grid grid-cols-[1fr_auto] gap-2">
        <div className="rounded-md bg-stone-50 p-2">
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">
            <Clock className="h-3.5 w-3.5 text-slate-500" />
            Online-Dauer
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-base font-bold text-slate-900">{status.marketing_days ?? '-'}</p>
              <p className="text-[10px] text-stone-500">Tage</p>
            </div>
            <div>
              <p className="text-base font-bold text-slate-900">{status.marketing_months ?? '-'}</p>
              <p className="text-[10px] text-stone-500">Monate</p>
            </div>
          </div>
        </div>
        <div className="w-16 rounded-md bg-rose-50 p-2">
          <TrendingDown className="mb-1 h-3.5 w-3.5 text-rose-600" />
          <p className="text-base font-bold text-rose-800">{status.price_reductions || 0}</p>
          <p className="text-[10px] text-rose-700">Reduz.</p>
        </div>
      </div>

      {timeline.length > 1 && (
        <div className="mb-3 h-16 rounded-md bg-slate-50 p-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeline}>
              <Area type="monotone" dataKey="value" stroke="#0f766e" fill="#99f6e4" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="space-y-2 text-xs text-stone-600">
        <div className="flex justify-between gap-3">
          <span>Online seit</span>
          <strong className="text-slate-800">{formatCompactDate(status.last_online_at)}</strong>
        </div>
        <div className="flex justify-between gap-3">
          <span>Offline seit</span>
          <strong className="text-slate-800">{formatCompactDate(status.last_offline_at)}</strong>
        </div>
        <div className="flex justify-between gap-3">
          <span>Antwort</span>
          <strong className="text-slate-800">{formatCompactDate(status.last_feedback_at)}</strong>
        </div>
        {status.latest_feedback?.reason_label && (
          <div className="mt-2 rounded-md bg-emerald-50 p-2 text-emerald-900">
            <div className="flex items-start gap-2">
              <MessageSquareWarning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{status.latest_feedback.reason_label}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [immoStatus, setImmoStatus] = useState(null);
  const [brokerList, setBrokerList] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeMarketingFilter, setActiveMarketingFilter] = useState('all');
  const [activeBrokers, setActiveBrokers] = useState([]);
  const [showInactive, setShowInactive] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isCompactHeight, setIsCompactHeight] = useState(false);
  const hasInitializedViewport = useRef(false);
  const hasHandledDeepLink = useRef(false);

  const defaultCenter = [48.1351, 11.582];
  const defaultZoom = 8;
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const syncViewport = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsCompactHeight(window.innerHeight < 760);
      setIsSidebarOpen((current) => {
        if (!hasInitializedViewport.current) {
          return !mobile;
        }
        return mobile ? current : true;
      });
      hasInitializedViewport.current = true;
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  const fetchBrokers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/brokers`, { withCredentials: true });
      setBrokerList(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Makler:', error);
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Immobilien:', error);
      if (error.response?.status === 401) {
        navigate('/login');
        return;
      }
      if (error.response?.status === 403) {
        navigate('/pending-approval');
        return;
      }
      toast.error('Fehler beim Laden der Immobilien');
    }
  }, [navigate]);

  const fetchImmoStatus = useCallback(async () => {
    if (!isAdmin) {
      setImmoStatus(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/immostatus`, { withCredentials: true });
      setImmoStatus(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Admin-Immostatus:', error);
    }
  }, [isAdmin]);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      await Promise.all([fetchBrokers(), fetchProperties(), fetchImmoStatus()]);
      setIsLoading(false);
    };

    loadInitialData();
  }, [fetchBrokers, fetchProperties, fetchImmoStatus]);

  useEffect(() => {
    if (isLoading || properties.length === 0 || hasHandledDeepLink.current) {
      return;
    }

    const propertyId = searchParams.get('property');
    const objektnrExtern = searchParams.get('objektnr_extern');
    const openimmoObid = searchParams.get('openimmo_obid');

    if (!propertyId && !objektnrExtern && !openimmoObid) {
      hasHandledDeepLink.current = true;
      return;
    }

    const matchedProperty = properties.find((property) => {
      if (propertyId && property.property_id === propertyId) {
        return true;
      }
      if (objektnrExtern && property.objektnr_extern === objektnrExtern) {
        return true;
      }
      if (openimmoObid && property.openimmo_obid === openimmoObid) {
        return true;
      }
      return false;
    });

    hasHandledDeepLink.current = true;

    if (matchedProperty) {
      setSelectedProperty(matchedProperty);
      setIsDetailOpen(true);
    }
  }, [isLoading, properties, searchParams]);

  const brokerFilteredProperties = useMemo(() => {
    return properties.filter((property) => {
      if (activeBrokers.length === 0) {
        return true;
      }
      return activeBrokers.includes(property.responsible_broker);
    });
  }, [properties, activeBrokers]);

  const filteredProperties = useMemo(() => {
    return brokerFilteredProperties.filter((property) => {
      if (!showInactive && property.is_active === false) {
        return false;
      }
      if (activeFilter !== 'all' && property.property_type !== activeFilter) {
        return false;
      }
      if (activeMarketingFilter !== 'all' && getMarketingFilterType(property) !== activeMarketingFilter) {
        return false;
      }
      return true;
    });
  }, [brokerFilteredProperties, showInactive, activeFilter, activeMarketingFilter]);

  const stats = useMemo(() => buildStats(brokerFilteredProperties), [brokerFilteredProperties]);

  const markerItems = useMemo(() => {
    return filteredProperties.filter(hasValidCoordinates).map((property) => ({
      property,
      icon: createMarkerIcon(property.property_type, property.is_active !== false),
    }));
  }, [filteredProperties]);

  const mappableProperties = useMemo(() => {
    return filteredProperties.filter(hasValidCoordinates);
  }, [filteredProperties]);

  const statusByPropertyKey = useMemo(() => {
    const entries = new Map();
    (immoStatus?.properties || []).forEach((status) => {
      [status.property_id, status.objektnr_extern, status.openimmo_obid, status.property_key].filter(Boolean).forEach((key) => {
        entries.set(String(key), status);
      });
    });
    return entries;
  }, [immoStatus]);

  const handleMarkerClick = (property) => {
    setSelectedProperty(property);
    setIsDetailOpen(true);
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).finally(() => {
      toast.success('Session beendet');
      navigate('/login?logout=1', { replace: true });
    });
  };

  const handleBrokerToggle = (broker) => {
    setActiveBrokers((current) => {
      if (current.includes(broker)) {
        return current.filter((entry) => entry !== broker);
      }
      return [...current, broker];
    });
    setActiveFilter('all');
    setActiveMarketingFilter('all');
  };

  const handleSelectAllBrokers = () => {
    setActiveBrokers([]);
    setActiveFilter('all');
    setActiveMarketingFilter('all');
  };

  const activeFilterLabel =
    activeFilter === 'all'
      ? 'Immobilien'
      : activeFilter === 'haus'
        ? 'Häuser'
        : activeFilter === 'wohnung'
          ? 'Wohnungen'
          : 'Grundstücke';
  const activeMarketingFilterLabel =
    activeMarketingFilter === 'rent'
      ? 'Vermietung'
      : activeMarketingFilter === 'sale'
        ? 'Verkauf'
        : '';
  const activeResultLabel = [activeMarketingFilterLabel, activeFilterLabel].filter(Boolean).join(' ');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4" />
          <p className="text-stone-600">Lade Immobilien...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden bg-stone-100" data-testid="dashboard">
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            {isMobile && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsSidebarOpen(false)}
                className="absolute inset-0 z-20 bg-slate-950/45 lg:hidden"
                aria-label="Sidebar schließen"
              />
            )}
            <Sidebar
              user={user}
              stats={stats}
              brokerList={brokerList}
              activeBrokers={activeBrokers}
              onBrokerToggle={handleBrokerToggle}
              onSelectAllBrokers={handleSelectAllBrokers}
              onLogout={handleLogout}
              onClose={() => setIsSidebarOpen(false)}
            />
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 relative min-w-0">
        {(!isSidebarOpen || isMobile) && (
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => setIsSidebarOpen(true)}
            className="absolute top-3 left-3 z-20 bg-slate-900 text-white p-2.5 rounded-full shadow-lg hover:bg-slate-800 transition-colors lg:hidden"
            data-testid="toggle-sidebar-btn"
            aria-label="Navigation öffnen"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </motion.button>
        )}

        <FilterPanel
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          activeMarketingFilter={activeMarketingFilter}
          setActiveMarketingFilter={setActiveMarketingFilter}
          showInactive={showInactive}
          setShowInactive={setShowInactive}
          stats={stats}
          isMobile={isMobile}
        />

        <div className="absolute inset-0" data-testid="map-container">
          <MapContainer center={defaultCenter} zoom={defaultZoom} style={{ height: '100%', width: '100%' }} zoomControl={false}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {mappableProperties.length > 0 && <MapBoundsUpdater properties={mappableProperties} />}

            {markerItems.map(({ property, icon }) => (
              <Marker
                key={property.property_id}
                position={[property.latitude, property.longitude]}
                icon={icon}
              >
                <Popup autoPan closeButton maxWidth={isAdmin ? 560 : 280} minWidth={isAdmin && !isMobile ? 520 : 240}>
                  <div className="flex max-w-[80vw] flex-col overflow-hidden rounded-lg bg-white sm:max-w-none sm:flex-row">
                    <PropertyCard property={property} onViewDetails={() => handleMarkerClick(property)} compact />
                    {isAdmin && (
                      <PropertyMapStatusPanel
                        status={
                          statusByPropertyKey.get(property.property_id) ||
                          statusByPropertyKey.get(property.objektnr_extern) ||
                          statusByPropertyKey.get(property.openimmo_obid)
                        }
                      />
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="pointer-events-none absolute bottom-3 left-3 right-3 z-10 sm:bottom-4 sm:left-1/2 sm:right-auto sm:w-auto sm:-translate-x-1/2">
          <div
            className={`glass pointer-events-auto flex items-center justify-between gap-3 ${
              isMobile ? 'rounded-2xl px-4 py-3' : 'rounded-full px-4 py-3 sm:px-6'
            }`}
          >
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-slate-900 text-base sm:text-lg">{filteredProperties.length}</span>
                <span className="text-stone-600 text-sm sm:text-base truncate">{activeResultLabel}</span>
              </div>
              {(isMobile || isCompactHeight) && (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500">
                  {activeBrokers.length > 0 && <span>{activeBrokers.length} Makler aktiv</span>}
                  {showInactive && stats.historical_total > 0 && <span>{stats.historical_total} historisch</span>}
                  {!showInactive && <span>nur aktive Objekte</span>}
                </div>
              )}
            </div>
            {!isMobile && !isCompactHeight && (
              <div className="flex items-center gap-2 text-sm text-stone-400">
                {activeBrokers.length > 0 && <span>{activeBrokers.length} Makler ausgewählt</span>}
                {activeBrokers.length > 0 && showInactive && stats.historical_total > 0 && <span>•</span>}
                {showInactive && stats.historical_total > 0 && <span>{stats.historical_total} historisch</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      <PropertyDetailDialog
        property={selectedProperty}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedProperty(null);
        }}
      />
    </div>
  );
}
